import {
    TypeProductCardWithoutUnresolvableLinksResponse,
    TypeProductContentHousingWithoutUnresolvableLinksResponse,
} from '@/features/contentful/_generated';
import AboutUsCard from '@/features/contentful/components/Cards/AboutUsCard';
import FeaturedProductCard from '@/features/contentful/components/Cards/FeaturedProductCard';
import FeaturedProductList from '@/features/contentful/components/FeaturedProductList/FeaturedProductList';
import TileContainer from '@/features/contentful/components/Tile/TileContainer';
import TileContent from '@/features/contentful/components/Tile/TileContent';
import TileHeader from '@/features/contentful/components/Tile/TileHeader';
import classNames from 'classnames';
import React from 'react';
import CarouselCard from '@/components/Carousel/CarouselCard';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import ImageListing from '@/features/contentful/components/ImageListing/ImageListing';
import SwiperCarousel from '@/components/Carousel/SwiperCarousel';
import { getLocalizedLink } from '@/utils/getLocalizedLink';
import { useSettingsContext } from '@/contexts/SettingsContext';

interface IProductCardProps {
    component: TypeProductContentHousingWithoutUnresolvableLinksResponse;
    id?: string;
    type?: TypeProductContentHousingWithoutUnresolvableLinksResponse['fields']['type'];
}

const ProductCard: React.FC<IProductCardProps> = ({
    component: { fields },
    id,
    type = 'Default',
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });
    const { lang } = useSettingsContext();

    return (
        <TileContainer
            className={classNames({
                'tw-bg-bgSkyLight': fields?.backgroundColor === 'blue',
                'tw-bg-bgNavy': fields?.backgroundColor === 'dark blue',
            })}
            paddingTop={fields?.paddingTop}
            paddingBottom={fields?.paddingBottom}
        >
            <TileHeader
                id={id}
                title={fields?.title}
                subtitle={fields?.subtitle}
                isDarkMode={fields?.backgroundColor === 'dark blue'}
                titlePosition={fields?.titlePosition}
                subtitlePosition={fields?.subtitlePosition}
            />

            {type === 'Carousel' ? (
                <SwiperCarousel config={{}}>
                    {fields?.ourComponent?.map((item) => {
                        const productItem = item as TypeProductCardWithoutUnresolvableLinksResponse;
                        return (
                            <div
                                {...inspectorProps({
                                    fieldId: 'title',
                                })}
                                key={productItem?.sys?.id}
                            >
                                <CarouselCard
                                    id={productItem?.sys?.id}
                                    image={
                                        productItem?.fields?.image?.fields?.file
                                            ? `https:${productItem?.fields?.image?.fields?.file?.url}?fm=webp`
                                            : undefined
                                    }
                                    imageAlt={productItem?.fields?.image?.fields?.title}
                                    title={productItem?.fields?.title}
                                    path={
                                        productItem?.fields?.link ??
                                        productItem?.fields?.asset?.fields?.file?.url
                                    }
                                    target={productItem?.fields?.target}
                                    summary={productItem?.fields?.content}
                                    linkText={productItem?.fields?.buttonLabel ?? 'Learn More'}
                                />{' '}
                            </div>
                        );
                    })}
                </SwiperCarousel>
            ) : type === 'Home big cards' ? (
                <ImageListing
                    imageCardContent={fields?.ourComponent?.map((item) => {
                        const productItem = item as TypeProductCardWithoutUnresolvableLinksResponse;
                        return {
                            title: productItem?.fields?.title ?? '',
                            subtitle: productItem?.fields?.content as unknown as Document,
                            image: productItem?.fields?.image?.fields?.file
                                ? `https:${productItem?.fields?.image?.fields?.file?.url}?fm=webp`
                                : '',
                            buttonText: productItem?.fields?.buttonLabel ?? '',
                            link: productItem?.fields?.link
                                ? getLocalizedLink(productItem?.fields?.link, lang)
                                : '',
                            target: productItem?.fields?.target ?? '',
                        };
                    })}
                />
            ) : (
                <TileContent
                    className={classNames({
                        '!tw-block !tw-px-0': type === 'Side cards',
                    })}
                >
                    {fields.type === 'Side cards' ? (
                        <FeaturedProductList
                            options={fields?.ourComponent.map((card) => ({
                                label: card?.fields?.title ?? '',
                                id: card?.sys?.id ?? '',
                            }))}
                        >
                            {fields.ourComponent.map((item) => {
                                const card =
                                    item as TypeProductCardWithoutUnresolvableLinksResponse;
                                return (
                                    <FeaturedProductCard
                                        key={card?.sys.id}
                                        id={card?.sys.id}
                                        title={card?.fields?.title ?? ''}
                                        summary={card?.fields?.content ?? ''}
                                        image={
                                            card?.fields?.image?.fields?.file
                                                ? `https:${card?.fields.image.fields.file.url}?fm=webp`
                                                : undefined
                                        }
                                        buttonLabel={card?.fields?.buttonLabel ?? ''}
                                        path={card?.fields?.link ?? '/'}
                                        target={card?.fields?.target}
                                    />
                                );
                            })}
                        </FeaturedProductList>
                    ) : (
                        fields?.ourComponent?.map((item) => {
                            const productItem =
                                item as TypeProductCardWithoutUnresolvableLinksResponse;
                            return (
                                <AboutUsCard
                                    key={productItem?.sys?.id}
                                    id={productItem?.sys?.id}
                                    image={
                                        productItem?.fields?.image?.fields?.file
                                            ? `https:${productItem?.fields?.image?.fields?.file?.url}?fm=webp`
                                            : undefined
                                    }
                                    imageDescription={
                                        productItem?.fields?.image?.fields?.title ?? ''
                                    }
                                    icon={
                                        productItem?.fields?.icon
                                            ? `https:${productItem?.fields?.icon?.fields?.file?.url}`
                                            : undefined
                                    }
                                    title={productItem?.fields?.title}
                                    description={productItem?.fields?.content}
                                    link={productItem?.fields?.link}
                                    linkText={productItem?.fields?.buttonLabel ?? 'Learn More'}
                                    target={productItem?.fields?.target}
                                    file={productItem?.fields?.asset?.fields?.file}
                                />
                            );
                        })
                    )}
                </TileContent>
            )}
        </TileContainer>
    );
};

export default ProductCard;
